<script setup>
import { onBeforeUnmount, onMounted, shallowRef } from 'vue'

defineOptions({
  name: 'common-header-billing-cart'
})

const PERSISTENT_CART_LOADING_BUFFER_TIME = 20000

const microsDatas = window?.microsDatas || {}
const displayPersistentCartV2 = Boolean(microsDatas.displayPersistentCartV2)
const placeholderCartCount = 0

const persistentCartTarget = shallowRef(null)
const isPersistentCartLoading = shallowRef(true)
const loadingBufferTimer = shallowRef(null)

function getPersistentCart() {
  return window?.__persistent_cart__
}

function clearLoadingBufferTimer() {
  if (!loadingBufferTimer.value) return
  window.clearTimeout(loadingBufferTimer.value)
  loadingBufferTimer.value = null
}

function finishPersistentCartLoading() {
  clearLoadingBufferTimer()
  isPersistentCartLoading.value = false
}

function checkPersistentCartAfterBuffer() {
  getPersistentCart()
  finishPersistentCartLoading()
}

function startPersistentCartLoadingBuffer() {
  isPersistentCartLoading.value = true
  clearLoadingBufferTimer()
  loadingBufferTimer.value = window.setTimeout(() => {
    checkPersistentCartAfterBuffer()
  }, PERSISTENT_CART_LOADING_BUFFER_TIME)
}

function executeAfterPersistentCart() {
  if (!displayPersistentCartV2 || !persistentCartTarget.value) return
  startPersistentCartLoadingBuffer()
}

onMounted(() => {
  if (displayPersistentCartV2) {
    void executeAfterPersistentCart()
  }
})

onBeforeUnmount(() => {
  clearLoadingBufferTimer()
})
</script>

<template>
  <div
    v-if="displayPersistentCartV2"
    id="billing-persistent-cart-wrapper"
    ref="persistentCartTarget"
    class="billing-shopping-bag__container">
    <div
      :class="[
        'billing-cart-placeholder',
        'zbo-popover__trigger',
        { 'billing-cart-placeholder--loading': isPersistentCartLoading }
      ]"
      tabindex="0"
      role="button"
      :aria-busy="isPersistentCartLoading"
      :aria-disabled="isPersistentCartLoading"
      :aria-label="isPersistentCartLoading ? $t('aria.cartLoading') : $t('aria.cart')"
      aria-live="polite"
      aria-atomic="true">
      <zm-spinners
        v-if="isPersistentCartLoading"
        size="sm"
        class="billing-cart-placeholder__spinner" />
      <template v-else>
        <svg
          class="billing-cart-placeholder__icon"
          width="19"
          height="18"
          viewBox="0 0 19 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true">
          <path
            d="M2.93262 0C3.77616 0.000116086 4.4784 0.603573 4.60156 1.43555C4.70936 2.16522 4.83556 3.02615 4.96973 3.9375H17.375C18.0301 3.9375 18.5462 4.49524 18.4961 5.14844L18.1338 9.87012C18.0353 11.1523 17.0813 12.1781 15.8486 12.4102C15.8101 12.4186 15.7736 12.4336 15.7324 12.4336C15.7252 12.4335 15.7191 12.429 15.7119 12.4287C15.6294 12.4401 15.5496 12.4598 15.4648 12.4639L13.7432 12.5469H13.7373L11.7461 12.6426C11.7429 12.6427 11.7386 12.6434 11.7354 12.6436L5.86816 12.9258L5.52539 13.2471C5.2211 13.5338 5.04492 13.9393 5.04492 14.3584V15.1875H14.8525C14.8718 15.1875 14.8881 15.1974 14.9072 15.1982C14.9638 15.204 15.0209 15.2102 15.0713 15.2314C15.7395 15.3403 16.2588 15.8947 16.2588 16.5938C16.2588 17.3704 15.6293 18 14.8525 18C14.0761 17.9998 13.4463 17.3703 13.4463 16.5938C13.4463 16.494 13.4841 16.4064 13.5039 16.3125H5.83203C5.85181 16.4064 5.88867 16.494 5.88867 16.5938C5.88867 17.3703 5.2598 17.9998 4.4834 18C3.70666 18 3.07715 17.3704 3.07715 16.5938C3.07715 16.0181 3.42453 15.5262 3.91992 15.3086V14.3584C3.91992 13.6301 4.22433 12.9255 4.75488 12.4268L5.03516 12.1641C4.96197 11.6509 4.8793 11.0748 4.79004 10.4619C4.78566 10.4322 4.78074 10.4021 4.77637 10.3721C4.73778 10.1075 4.699 9.83727 4.65918 9.56543L4.6582 9.56152C4.2612 6.85057 3.82617 3.9375 3.82617 3.9375H3.83301C3.70779 3.08756 3.58948 2.28616 3.48828 1.60059C3.44765 1.32492 3.21376 1.12512 2.93262 1.125H1.0625C0.751584 1.125 0.5 0.873416 0.5 0.5625C0.5 0.251584 0.751595 0 1.0625 0H2.93262ZM6.36035 11.4941L8.33496 11.3984L10.3086 11.3037L14.2578 11.1143L15.1699 11.0703C15.4466 11.0359 16.0589 10.9079 16.2949 10.6719C16.452 10.5228 16.5595 10.3328 16.6348 10.125C16.7097 9.91812 16.7893 9.00846 16.79 9L16.9844 6.46875L17.0713 5.34375H5.45703L6.36035 11.4941Z" />
        </svg>
        <span class="billing-cart-placeholder__count">{{ placeholderCartCount }}</span>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.billing-shopping-bag__container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.billing-cart-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 32px;
  padding: 6px 12px;
  border-radius: 12px;
  background-color: #0d6bde;
  color: #ffffff;
  box-sizing: border-box;
}

.billing-cart-placeholder--loading {
  min-width: 59px;
  cursor: default;
}

.billing-cart-placeholder__icon {
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
  fill: currentcolor;
}

.billing-cart-placeholder__count {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: inherit;
}

.billing-cart-placeholder__spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.48);

  :deep(.zoom-spinners__spinner) {
    background-color: rgba(255, 255, 255, 0.48);
  }

  :deep(.zoom-spinners) {
    display: inline-flex;
    width: 16px;
    height: 16px;
  }
}
</style>
